// termui — regex-based syntax tokenizer for TCode

export type TokenType =
  | "keyword"
  | "string"
  | "number"
  | "comment"
  | "punctuation"
  | "function"
  | "type"
  | "default";

export type Token = { text: string; type: TokenType };

type Rule = { pattern: RegExp; type: TokenType };

function rules(pairs: [RegExp, TokenType][]): Rule[] {
  return pairs.map(([pattern, type]) => ({
    pattern: new RegExp(pattern.source, "y"),
    type,
  }));
}

const JS_RULES = rules([
  [/\/\/.*/, "comment"],
  [/\/\*[\s\S]*?\*\//, "comment"],
  [/"(?:[^"\\]|\\.)*"/, "string"],
  [/'(?:[^'\\]|\\.)*'/, "string"],
  [/`(?:[^`\\]|\\.)*`/, "string"],
  [/\b(?:const|let|var|function|return|if|else|for|while|do|class|import|export|from|async|await|new|throw|try|catch|finally|typeof|instanceof|in|of|void|delete|switch|case|break|continue|default|yield|extends|implements|interface|type|as|this|super|null|undefined|true|false)\b/, "keyword"],
  [/\b\d[\d_.]*(?:\.\d[\d_]*)?(?:[eE][+-]?\d+)?\b/, "number"],
  [/\b[A-Z][a-zA-Z0-9]*\b/, "type"],
  [/\b[a-zA-Z_$][\w$]*(?=\s*\()/, "function"],
  [/=>/, "punctuation"],
  [/[{}()\[\];:.,<>=!+\-*/%&|?@^~]/, "punctuation"],
]);

const JSON_RULES = rules([
  [/"(?:[^"\\]|\\.)*"(?=\s*:)/, "function"],
  [/"(?:[^"\\]|\\.)*"/, "string"],
  [/\b(?:true|false|null)\b/, "keyword"],
  [/-?\b\d[\d_.]*(?:\.\d[\d_]*)?(?:[eE][+-]?\d+)?\b/, "number"],
  [/[{}\[\]:,]/, "punctuation"],
]);

const BASH_RULES = rules([
  [/#.*/, "comment"],
  [/"(?:[^"\\]|\\.)*"/, "string"],
  [/'[^']*'/, "string"],
  [/\$\{[^}]*\}/, "type"],
  [/\$[A-Za-z_]\w*/, "type"],
  [/\b(?:if|then|else|elif|fi|for|do|done|while|until|case|esac|function|in|return|exit|echo|export|local|readonly|source|alias|cd|ls|rm|cp|mv|mkdir|grep|sed|awk|cat|chmod|chown|sudo|apt|brew|npm|npx|bun|yarn|git|docker|curl|wget|pipe)\b/, "keyword"],
  [/\b\d+\b/, "number"],
  [/[|;&<>()\[\]{}]/, "punctuation"],
]);

const GENERIC_RULES = rules([
  [/\/\/.*/, "comment"],
  [/#.*/, "comment"],
  [/"(?:[^"\\]|\\.)*"/, "string"],
  [/'(?:[^'\\]|\\.)*'/, "string"],
  [/\b\d[\d_.]*(?:\.\d[\d_]*)?(?:[eE][+-]?\d+)?\b/, "number"],
  [/\b[A-Z][a-zA-Z0-9]*\b/, "type"],
  [/[{}()\[\];:.,<>=!+\-*/%&|?@^~]/, "punctuation"],
]);

function rulesFor(lang?: string): Rule[] {
  if (!lang) return GENERIC_RULES;
  const l = lang.toLowerCase();
  if (l === "json" || l === "jsonc") return JSON_RULES;
  if (l === "bash" || l === "sh" || l === "zsh" || l === "shell") return BASH_RULES;
  if (l === "js" || l === "jsx" || l === "ts" || l === "tsx" || l === "javascript" || l === "typescript") return JS_RULES;
  return GENERIC_RULES;
}

function tokenizeLine(line: string, langRules: Rule[]): Token[] {
  const tokens: Token[] = [];
  let pos = 0;
  while (pos < line.length) {
    // Skip whitespace as default
    if (line[pos] === " " || line[pos] === "\t") {
      let ws = pos;
      while (ws < line.length && (line[ws] === " " || line[ws] === "\t")) ws++;
      tokens.push({ text: line.slice(pos, ws), type: "default" });
      pos = ws;
      continue;
    }
    let matched = false;
    for (const rule of langRules) {
      rule.pattern.lastIndex = pos;
      const m = rule.pattern.exec(line);
      if (m && m.index === pos) {
        tokens.push({ text: m[0], type: rule.type });
        pos += m[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      // Consume one char as default
      const start = pos;
      pos++;
      while (pos < line.length && line[pos] !== " " && line[pos] !== "\t") {
        let anyMatch = false;
        for (const rule of langRules) {
          rule.pattern.lastIndex = pos;
          const m = rule.pattern.exec(line);
          if (m && m.index === pos) { anyMatch = true; break; }
        }
        if (anyMatch) break;
        pos++;
      }
      tokens.push({ text: line.slice(start, pos), type: "default" });
    }
  }
  return tokens;
}

export function tokenize(source: string, language?: string): Token[][] {
  const langRules = rulesFor(language);
  return source.split("\n").map((line) => tokenizeLine(line, langRules));
}
